import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Checkbox,
  InputText,
  SectionCard,
  Select,
  type SelectOption,
} from "@/deps/ui-library";
import { useParties, usePartyDetails, useSaveParty, useWithholdingCodes } from "@/api/queries";
import type { Id, Party, PartyForm } from "@/api/types";
import { Dialog } from "@/components/Dialog";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { PermittedAction } from "@/components/PermittedAction";
import { useCan } from "@/app/session";
import { PAYMENT_TERMS } from "@/lib/paymentTerms";
import { useTaxWords } from "@/app/session";

const WORDS = {
  customer: { title: "Customers", one: "customer", icon: "Users" },
  supplier: { title: "Suppliers", one: "supplier", icon: "Building2" },
  employee: { title: "Employees", one: "employee", icon: "Users" },
} as const;

/**
 * Who a company sells to or buys from. A document cannot be entered without one, so this is the
 * first thing a new company needs after its books are set up.
 */
export function PartiesPage({ kind }: { kind: "customer" | "supplier" }) {
  const say = useTaxWords();
  const words = WORDS[kind];
  const can = useCan();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data: parties, isPending, error } = useParties(kind, search);
  const [editing, setEditing] = useState<Id | "new" | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={words.title}
        actions={
          <span className="flex flex-wrap gap-2">
            <PermittedAction
              permission="reports.view"
              variant="secondary"
              onClick={() =>
                void navigate({
                  to:
                    kind === "customer"
                      ? "/sales/receivable-analysis"
                      : "/purchases/payable-analysis",
                })
              }
            >
              {kind === "customer" ? "Receivable analysis" : "Payable analysis"}
            </PermittedAction>
            <PermittedAction permission="settings.parties.manage" onClick={() => setEditing("new")}>
              New {words.one}
            </PermittedAction>
          </span>
        }
      />

      <SectionCard title={words.title} flush>
        <div className="border-line-soft border-b p-3">
          <InputText
            aria-label={`Search ${words.one}s`}
            placeholder={say("Search by name or PAN")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {error ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(error as Error).message}
          />
        ) : isPending ? (
          <p className="text-fg-muted p-4 text-sm">Loading</p>
        ) : (parties?.length ?? 0) === 0 ? (
          <EmptyState
            icon={words.icon}
            title={search === "" ? `No ${words.one}s yet` : `No ${words.one}s match`}
            description={
              search === ""
                ? `Add a ${words.one} to enter your first document for them.`
                : say("Try another name or PAN.")
            }
          />
        ) : (
          <ul className="flex flex-col">
            {parties!.map((party) => (
              <PartyRow
                key={party.id}
                party={party}
                kind={kind}
                onEdit={can("settings.parties.manage") ? () => setEditing(party.id) : undefined}
              />
            ))}
          </ul>
        )}
      </SectionCard>

      {editing === "new" ? (
        <PartyDialog kind={kind} onClose={() => setEditing(null)} />
      ) : editing !== null ? (
        <EditParty id={editing} kind={kind} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}

function PartyRow({
  party,
  kind,
  onEdit,
}: {
  party: Party;
  kind: "customer" | "supplier";
  onEdit: (() => void) | undefined;
}) {
  const say = useTaxWords();
  return (
    <li className="border-line-soft flex items-center gap-3 border-b px-4 py-2.5 last:border-0">
      <span className="min-w-0 flex-1">
        {kind === "customer" ? (
          <Link
            to="/sales/customers/$id"
            params={{ id: party.id }}
            className="text-fg block text-sm hover:underline"
          >
            {party.name}
          </Link>
        ) : (
          <Link
            to="/purchases/suppliers/$id"
            params={{ id: party.id }}
            className="text-fg block text-sm hover:underline"
          >
            {party.name}
          </Link>
        )}
        {party.pan ? (
          <span className="text-fg-hint num block text-xs">
            {say("PAN")} {party.pan}
          </span>
        ) : null}
      </span>
      {onEdit ? (
        <Button colorScheme="default" variant="outline" size="sm" onClick={onEdit}>
          Edit
        </Button>
      ) : null}
    </li>
  );
}

function EditParty({
  id,
  kind,
  onClose,
}: {
  id: Id;
  kind: "customer" | "supplier";
  onClose: () => void;
}) {
  const { data } = usePartyDetails(id);
  return data ? <PartyDialog kind={kind} id={id} initial={data} onClose={onClose} /> : null;
}

const EMPTY: Omit<PartyForm, "kind"> = {
  legalName: "",
  tradingName: "",
  pan: "",
  vatRegistered: false,
  legalRegistration: "",
  street: "",
  city: "",
  phone: "",
  email: "",
  creditLimit: "",
  paymentTermsDays: "",
  withholdingCode: "",
};

export function PartyDialog({
  kind,
  id,
  initial,
  onClose,
}: {
  kind: "customer" | "supplier" | "employee";
  id?: Id;
  initial?: PartyForm;
  onClose: () => void;
}) {
  const say = useTaxWords();
  const words = WORDS[kind];
  const [form, setForm] = useState<PartyForm>(initial ?? { ...EMPTY, kind });
  const save = useSaveParty();
  const { data: codes } = useWithholdingCodes();
  const field = (
    key:
      | "legalName"
      | "tradingName"
      | "pan"
      | "legalRegistration"
      | "street"
      | "city"
      | "phone"
      | "email",
  ) => ({
    value: form[key],
    onChange: (event: { target: { value: string } }) =>
      setForm({ ...form, [key]: event.target.value }),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={id ? `Edit ${words.one}` : `New ${words.one}`}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={
            form.legalName.trim() === "" ||
            save.isPending ||
            !/^(\d+(\.\d{1,2})?)?$/.test(form.creditLimit.trim())
          }
          onClick={() => save.mutate({ id, form }, { onSuccess: onClose })}
        >
          {save.isPending ? "Saving" : `Save ${words.one}`}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(save.error as Error).message}
          />
        ) : null}
        <InputText label="Legal name" required {...field("legalName")} />
        <InputText label="Trading name" {...field("tradingName")} />
        <InputText label={say("PAN")} {...field("pan")} />
        {form.kind === "employee" ? null : (
          <InputText label="Company registration" {...field("legalRegistration")} />
        )}
        {kind === "employee" ? null : (
          <Checkbox
            checked={form.vatRegistered}
            onCheckedChange={(details) =>
              setForm({ ...form, vatRegistered: details.checked === true })
            }
            label={say("Registered for VAT")}
          />
        )}
        <InputText label="Street" {...field("street")} />
        <InputText label="City" {...field("city")} />
        <InputText label="Phone" {...field("phone")} />
        <InputText label="Email" type="email" {...field("email")} />
        {kind === "employee" ? null : (
          <Select
            label="Payment terms"
            options={PAYMENT_TERMS}
            value={form.paymentTermsDays}
            onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
              const option = Array.isArray(details.value) ? details.value[0] : details.value;
              setForm({ ...form, paymentTermsDays: option ? String(option.value) : "" });
            }}
            showClearButton={false}
          />
        )}
        {kind === "supplier" ? (
          <Select
            label="Withholding category"
            options={[
              { value: "", label: "No withholding" },
              ...(codes ?? []).map((code) => ({
                value: code.code,
                label: code.ratePct ? `${code.name} · ${code.ratePct}%` : code.name,
              })),
            ]}
            value={form.withholdingCode}
            onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
              const option = Array.isArray(details.value) ? details.value[0] : details.value;
              setForm({ ...form, withholdingCode: option ? String(option.value) : "" });
            }}
            showClearButton={false}
          />
        ) : null}
        {kind === "customer" ? (
          <InputText
            label="Credit limit"
            inputMode="decimal"
            placeholder="No limit"
            value={form.creditLimit}
            onChange={(event) => setForm({ ...form, creditLimit: event.target.value })}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
